'use client';

import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { DataTable } from '@/components/data-table/data-table';
import { useDataTable } from '@/hooks/use-data-table';
import { DataTableSearch } from '@/components/data-table/data-table-search';
import { useOrgNavigate } from '@/hooks/use-app-navigate';

import { getOrdersTableColumns } from './orders-table-columns';
import { OrdersTableActionBar } from './orders-table-action-bar';
import { OrdersTableSkeleton } from './OrdersTableSkeleton';
import { MainTableState } from '@/lib/utils/table-state-utils';
import { Order } from '@tagadapay/crm-entities';
import type { TableConfig } from '@/types/data-table';
import { DEFAULT_SKELETON_ROWS } from '@/config/data-table';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useToBePaidCount, useUnsyncedPaidCount, useStorefrontSyncCapability } from '@/lib/api/orders';

interface OrdersTableProps {
  orders: Order.Order[];
  total: number;
  pageCount: number;
  options: {
    'orders.status': string[];
    'stores.id': { id: string; name: string }[];
  };
  loading: boolean;
  searchQuery?: string;
  onSearchChange?: (value: string) => void;
  onTableStateChange: (state: MainTableState) => void;
  onRefundOrder: (orderId: string) => void;
  onSyncOrder: (orderId: string) => void;
  onDeleteOrders?: (orderIds: string[]) => void;
  onExportOrders?: () => void;
  isRefunding: boolean;
  isSyncing: boolean;
  isDeleting?: boolean;
  onRefetch: () => void;
  config?: TableConfig;
}

/**
 * OrdersTable component for displaying orders with DataTable functionality
 */
export function OrdersTable({
  orders,
  total,
  pageCount: _pageCount,
  options,
  loading,
  searchQuery = '',
  onSearchChange,
  onTableStateChange,
  onRefundOrder,
  onSyncOrder,
  onDeleteOrders,
  onExportOrders,
  isRefunding,
  isSyncing,
  isDeleting = false,
  onRefetch,
  config = {},
}: OrdersTableProps) {
  const { t, i18n } = useTranslation('orders');
  const navigate = useNavigate();
  const { orgRoutes } = useOrgNavigate();

  const handleRowClick = React.useCallback(
    (order: Order.Order) => {
      navigate(orgRoutes.ORDER_DETAILS(order.id));
    },
    [navigate, orgRoutes],
  );

  // Memoize columns but recreate when options change
  // Use a simple count as dependency to ensure React detects changes
  const optionsCount = (options?.['orders.status']?.length || 0) + (options?.['stores.id']?.length || 0);

  const { data: syncCapability } = useStorefrontSyncCapability();
  const canSyncOrders = syncCapability?.hasEnabledStorefront === true;
  const syncableStoreIds = React.useMemo(
    () => new Set(syncCapability?.storeIds ?? []),
    [syncCapability?.storeIds],
  );

  const columns = React.useMemo(
    () =>
      getOrdersTableColumns({
        onRefundOrder,
        onSyncOrder,
        isRefunding,
        isSyncing,
        options,
        t,
        syncableStoreIds,
      }),
    [onRefundOrder, onSyncOrder, isRefunding, isSyncing, options, t, i18n.language, syncableStoreIds],
  );

  // Track if options have ever loaded (only changes once)
  const hasLoadedOptionsRef = React.useRef(false);
  if (!hasLoadedOptionsRef.current && optionsCount > 0) {
    hasLoadedOptionsRef.current = true;
  }
  const tableKey = `${hasLoadedOptionsRef.current ? 'loaded' : 'loading'}-${i18n.language}`;

  const { table } = useDataTable({
    data: orders,
    columns,
    total,
    config,
    onTableStateChange,
    getRowId: (row: Order.Order) => row.id,
  });

  const { data: toBePaidCount } = useToBePaidCount();
  const { data: unsyncedPaidCount } = useUnsyncedPaidCount(canSyncOrders);
  const statusFilterValue = table.getColumn('status')?.getFilterValue() as string[] | undefined;
  const isToBePaidActive = statusFilterValue?.includes('to_be_paid') ?? false;
  const isUnsyncedPaidActive = !!table.getState().columnFilters.find((f) => f.id === 'unsyncedPaid')?.value;

  const toggleToBePaidFilter = React.useCallback(() => {
    const statusColumn = table.getColumn('status');
    // Clear unsynced filter when toggling to_be_paid
    table.setColumnFilters((prev) => prev.filter((f) => f.id !== 'unsyncedPaid' && f.id !== 'excludeDrafts'));
    if (isToBePaidActive) {
      statusColumn?.setFilterValue(undefined);
    } else {
      statusColumn?.setFilterValue(['to_be_paid']);
      table.setColumnFilters((prev) => [
        ...prev,
        { id: 'excludeDrafts', value: true },
      ]);
    }
  }, [table, isToBePaidActive]);

  const toggleUnsyncedPaidFilter = React.useCallback(() => {
    if (isUnsyncedPaidActive) {
      table.setColumnFilters((prev) => prev.filter((f) => f.id !== 'unsyncedPaid' && f.id !== 'excludeDrafts'));
    } else {
      // Clear status filter and set unsynced
      table.getColumn('status')?.setFilterValue(undefined);
      table.setColumnFilters((prev) => [
        ...prev.filter((f) => f.id !== 'unsyncedPaid' && f.id !== 'excludeDrafts'),
        { id: 'unsyncedPaid', value: true },
      ]);
    }
  }, [table, isUnsyncedPaidActive]);

  return (
    <DataTable
      key={tableKey}
      table={table}
      onExport={onExportOrders}
      actionBar={
        <OrdersTableActionBar
          table={table}
          onDelete={onDeleteOrders}
          onExport={onExportOrders}
          isDeleting={isDeleting}
        />
      }
      isLoading={loading}
      skeletonRows={<OrdersTableSkeleton rows={config.skeletonRows || DEFAULT_SKELETON_ROWS} />}
      onRefresh={onRefetch}
      onRowClick={handleRowClick}
      isDraft={(order) => order.draft === true}
      extraFilters={
        <>
          <Button
            variant={isToBePaidActive ? 'default' : 'outline'}
            size="sm"
            className={cn(
              toBePaidCount?.total && !isToBePaidActive && "animate-pulse border-emerald-500/50"
            )}
            onClick={toggleToBePaidFilter}
          >
            {t('table.filters.toBePaid')}
            {toBePaidCount?.total ? (
              <span className={cn(
                "ml-1.5 rounded-full px-1.5 py-0.5 text-[10px] font-bold",
                isToBePaidActive
                  ? "bg-white/20 text-white"
                  : "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
              )}>
                {toBePaidCount.total}{toBePaidCount.approximate ? '+' : ''}
              </span>
            ) : null}
          </Button>
          {canSyncOrders ? (
          <Button
            variant={isUnsyncedPaidActive ? 'destructive' : 'outline'}
            size="sm"
            className={cn(
              unsyncedPaidCount?.total && !isUnsyncedPaidActive && "animate-pulse border-red-500/50"
            )}
            onClick={toggleUnsyncedPaidFilter}
          >
            {t('table.filters.unsyncedPaid')}
            {unsyncedPaidCount?.total ? (
              <span className={cn(
                "ml-1.5 rounded-full px-1.5 py-0.5 text-[10px] font-bold",
                isUnsyncedPaidActive
                  ? "bg-white/20 text-white"
                  : "bg-red-500/10 text-red-600 dark:text-red-400"
              )}>
                {unsyncedPaidCount.total}{unsyncedPaidCount.approximate ? '+' : ''}
              </span>
            ) : null}
          </Button>
          ) : null}
          <div className='w-full md:w-[200px]'>
            {onSearchChange && (
              <DataTableSearch value={searchQuery} onChange={onSearchChange} placeholder="Search anything..." />
            )}
          </div>
        </>
      }
    >

    </DataTable>
  );
}
